import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageHero from '../components/common/PageHero';
import { Card, CardContent } from '../components/ui/card';
import { BookOpen, FileText, Award, Download, Search, Video, Lock, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const CATEGORY_SLUG_MAP = {
  'cpd-courses': 'CPD Courses',
  'webinars': 'Webinars',
  'research-papers': 'Research Papers',
  'general': 'General',
  'videos': 'Videos',
};

const REVERSE_SLUG_MAP = Object.entries(CATEGORY_SLUG_MAP).reduce((acc, [slug, label]) => {
  acc[label] = slug;
  return acc;
}, {});

const Education = () => {
  const { category: urlCategory } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [accessMap, setAccessMap] = useState({});
  const [payingResource, setPayingResource] = useState(null);

  useEffect(() => {
    if (urlCategory && CATEGORY_SLUG_MAP[urlCategory]) {
      setCategoryFilter(CATEGORY_SLUG_MAP[urlCategory]);
    } else {
      setCategoryFilter('all');
    }
  }, [urlCategory]);

  useEffect(() => {
    fetchEducation();
  }, []);

  useEffect(() => {
    if (isAuthenticated && resources.length > 0) {
      checkAccessForPaidResources();
    }
  }, [isAuthenticated, resources]);

  const checkAccessForPaidResources = async () => {
    const paidResources = resources.filter(r => r.price > 0);
    if (paidResources.length === 0) return;
    const newAccessMap = {};
    await Promise.all(paidResources.map(async (r) => {
      try {
        const res = await axios.get(`${backendUrl}/api/education/${r.id}/access-check`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        newAccessMap[r.id] = res.data;
      } catch (e) {
        newAccessMap[r.id] = { has_access: false, price: r.price };
      }
    }));
    setAccessMap(newAccessMap);
  };

  const handleBuyAccess = async (resourceId) => {
    if (!isAuthenticated) {
      toast({ title: 'Login Required', description: 'Please login to purchase resources', variant: 'destructive' });
      navigate('/login');
      return;
    }
    try {
      setPayingResource(resourceId);
      const response = await axios.post(
        `${backendUrl}/api/payments/education/initiate`,
        { resource_id: resourceId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const { payfast_url, payment_data } = response.data;
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = payfast_url;
      Object.entries(payment_data).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = String(value);
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (error) {
      toast({ title: 'Payment Failed', description: error.response?.data?.detail || 'Failed to initiate payment', variant: 'destructive' });
    } finally {
      setPayingResource(null);
    }
  };

  const fetchEducation = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/education`);
      const data = response.data.resources || [];
      setResources(data);
      const cats = [...new Set(data.map(r => r.category).filter(Boolean))];
      setCategories(cats);
    } catch (error) {
      console.error('Error fetching education:', error);
      setResources([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredResources = resources.filter(r => {
    const matchesSearch = !searchQuery ||
      (r.title && r.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (r.description && r.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = categoryFilter === 'all' || r.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getCategoryIcon = (category) => {
    const cat = (category || '').toLowerCase();
    if (cat.includes('webinar') || cat.includes('video')) return Video;
    if (cat.includes('cert') || cat.includes('award')) return Award;
    if (cat.includes('paper') || cat.includes('research') || cat.includes('file')) return FileText;
    return BookOpen;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHero title="EDUCATION" />
      
      <div className="container mx-auto px-4 py-12">
        {!isAuthenticated && (
          <div className="mb-8 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
            <p className="text-blue-800">
              Please <a href="/login" className="underline font-semibold">login</a> to access exclusive educational resources and CPD courses.
            </p>
          </div>
        )}

        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Professional Development Resources
            </h2>
            <p className="text-lg text-gray-600">
              Access educational materials, courses, and resources to advance your sports chiropractic practice
            </p>
          </div>

          {/* Filters */}
          <div className="mb-8 flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search education resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2c3e7d]"
              />
            </div>
            {categories.length > 0 && (
              <select
                value={categoryFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  setCategoryFilter(val);
                  if (val === 'all') {
                    navigate('/education');
                  } else if (REVERSE_SLUG_MAP[val]) {
                    navigate(`/education/${REVERSE_SLUG_MAP[val]}`);
                  }
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2c3e7d]"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            )}
          </div>

          {/* Loading */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
            </div>
          ) : filteredResources.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Education Resources</h3>
                <p className="text-gray-600">Check back later for educational content.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredResources.map((resource) => {
                const IconComponent = getCategoryIcon(resource.category);
                return (
                  <Card key={resource.id} className="hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 bg-[#2c3e7d] bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <IconComponent size={32} className="text-[#2c3e7d]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            {resource.title}
                          </h3>
                          {resource.description && (
                            <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                              {resource.description}
                            </p>
                          )}
                          <div className="flex items-center gap-3 flex-wrap text-sm">
                            {resource.category && (
                              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                {resource.category}
                              </span>
                            )}
                            {resource.points != null && resource.points > 0 && (
                              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                                {resource.points} CPD points
                              </span>
                            )}
                            {resource.price > 0 && (
                              <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs flex items-center gap-1">
                                <DollarSign size={12} />
                                R {resource.price.toFixed(2)}
                              </span>
                            )}
                            {resource.created_at && (
                              <span className="text-gray-500 text-xs">
                                {formatDate(resource.created_at)}
                              </span>
                            )}
                          </div>

                          {/* Paid resource access control */}
                          {resource.price > 0 ? (
                            <div className="mt-4">
                              {accessMap[resource.id]?.has_access ? (
                                <>
                                  {resource.video_url && (
                                    <a href={resource.video_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#2c3e7d] hover:text-[#3d4f8e] text-sm font-medium">
                                      <Video size={16} /> Watch Video
                                    </a>
                                  )}
                                  {resource.resource_url && (
                                    <a href={resource.resource_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#2c3e7d] hover:text-[#3d4f8e] text-sm font-medium">
                                      <Download size={16} /> Download resource
                                    </a>
                                  )}
                                  {resource.files && resource.files.length > 0 && (
                                    <div className="space-y-2">
                                      {resource.files.map((file, idx) => (
                                        <a key={idx} href={file.url || '#'} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#2c3e7d] hover:text-[#3d4f8e] text-sm font-medium">
                                          <Download size={16} /> {file.name || `File ${idx + 1}`}
                                        </a>
                                      ))}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <Button
                                  onClick={() => handleBuyAccess(resource.id)}
                                  disabled={payingResource === resource.id}
                                  className="bg-[#2c3e7d] hover:bg-[#1e2a5a]"
                                  size="sm"
                                >
                                  {payingResource === resource.id ? (
                                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div> Processing...</>
                                  ) : (
                                    <><Lock size={16} className="mr-2" /> Buy Access - R {resource.price.toFixed(2)}</>
                                  )}
                                </Button>
                              )}
                            </div>
                          ) : (
                            <>
                              {/* Free resource - show files directly */}
                              {resource.files && resource.files.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  {resource.files.map((file, idx) => (
                                    <a
                                      key={idx}
                                      href={file.url || '#'}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-2 text-[#2c3e7d] hover:text-[#3d4f8e] text-sm font-medium"
                                    >
                                      <Download size={16} />
                                      {file.name || `File ${idx + 1}`}
                                    </a>
                                  ))}
                                </div>
                              )}
                              {resource.file_url && (
                                <a
                                  href={resource.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mt-3 inline-flex items-center gap-2 text-[#2c3e7d] hover:text-[#3d4f8e] text-sm font-medium"
                                >
                                  <Download size={16} />
                                  Download resource
                                </a>
                              )}
                              {resource.video_url && (
                                <a href={resource.video_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-[#2c3e7d] hover:text-[#3d4f8e] text-sm font-medium">
                                  <Video size={16} /> Watch Video
                                </a>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {isAuthenticated && (
            <div className="mt-12 bg-white rounded-lg shadow-md p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Your CPD Progress
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Total CPD Points Earned:</span>
                  <span className="text-2xl font-bold text-[#2c3e7d]">
                    {user?.points || 0} points
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div 
                    className="bg-[#2c3e7d] h-4 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((user?.points || 0) / 50 * 100, 100)}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600">
                  Annual requirement: 50 CPD points
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Education;
